/* VIP collection bar — delivery-date display, ZIP deliverability cell,
   and grid-density toggle. Shares vip:delivery-zip localStorage with the
   PDP delivery scheduler; all values are customer-facing mirrors of the
   delivery_settings/delivery_zone metaobjects — checkout is authoritative. */

(() => {
  const configEl = document.getElementById('VipCollectionBarConfig');
  if (!configEl) return;

  let config = {};
  try {
    config = JSON.parse(configEl.textContent);
  } catch {
    return;
  }

  /* the t filter HTML-escapes translations; decode for textContent use */
  const decoder = document.createElement('textarea');
  const decode = (value) => {
    decoder.innerHTML = value || '';
    return decoder.value;
  };
  const strings = {};
  for (const key of Object.keys(config.strings || {})) strings[key] = decode(config.strings[key]);

  /* ---- earliest deliverable date (mirrors the PDP scheduler rules) ---- */

  function zonedNowParts() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: config.timezone || 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type)?.value;
    return {
      date: `${get('year')}-${get('month')}-${get('day')}`,
      minutes: Number(get('hour')) * 60 + Number(get('minute')),
    };
  }

  function addDays(iso, days) {
    const d = new Date(`${iso}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  }

  function earliestDate() {
    const blackouts = config.blackoutDates || [];
    const [ch, cm] = String(config.cutoffTime || '15:00').split(':').map(Number);
    const now = zonedNowParts();
    let candidate = now.date;
    if (!config.sameDayEnabled || now.minutes >= ch * 60 + (cm || 0)) candidate = addDays(candidate, 1);
    for (let i = 0; i < 30 && blackouts.includes(candidate); i += 1) candidate = addDays(candidate, 1);
    return candidate;
  }

  function formatShort(iso) {
    return new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  }

  const blackouts = config.blackoutDates || [];
  const min = earliestDate();
  const max = addDays(zonedNowParts().date, Number(config.maxLeadDays) || 60);

  const nextValid = (iso) => {
    let candidate = iso < min ? min : iso;
    for (let i = 0; i < 30 && blackouts.includes(candidate); i += 1) candidate = addDays(candidate, 1);
    return candidate > max ? min : candidate;
  };

  const updateChips = (iso) => {
    const text = formatShort(iso);
    document.querySelectorAll('[data-vip-receive]').forEach((chip) => {
      const slot = chip.querySelector('[data-vip-receive-date]');
      if (slot) slot.textContent = text;
      chip.hidden = false;
    });
  };

  const saveDate = (iso) => {
    try {
      localStorage.setItem('vip:delivery-date', iso);
    } catch {
      /* non-essential */
    }
  };

  const dateCell = document.querySelector('[data-vip-bar-date]');
  const dateInput = dateCell?.querySelector('[data-vip-delivery-date]');
  let selected = min;
  try {
    const saved = localStorage.getItem('vip:delivery-date');
    if (saved && saved >= min && saved <= max && !blackouts.includes(saved)) selected = saved;
  } catch {
    /* non-essential */
  }

  if (dateInput) {
    dateInput.min = min;
    dateInput.max = max;
    dateInput.value = selected;
    dateInput.addEventListener('change', () => {
      const picked = nextValid(dateInput.value || min);
      dateInput.value = picked;
      saveDate(picked);
      updateChips(picked);
    });
    dateCell.hidden = false;
  }

  updateChips(selected);

  /* ---- grid density toggle ---- */

  const grid = document.getElementById('product-grid');
  const densityButtons = document.querySelectorAll('[data-vip-density-btn]');
  if (grid && densityButtons.length) {
    const apply = (density, persist) => {
      grid.dataset.vipDensity = density;
      densityButtons.forEach((btn) => {
        btn.setAttribute('aria-pressed', btn.dataset.vipDensityBtn === density ? 'true' : 'false');
      });
      if (persist) {
        try {
          localStorage.setItem('vip:collection-density', density);
        } catch {
          /* non-essential */
        }
      }
    };
    densityButtons.forEach((btn) => {
      btn.addEventListener('click', () => apply(btn.dataset.vipDensityBtn, true));
    });
    let saved = null;
    try {
      saved = localStorage.getItem('vip:collection-density');
    } catch {
      /* non-essential */
    }
    if (saved && ['2', '3', '4'].includes(saved)) apply(saved, false);
  }

  /* ---- ZIP cell ---- */

  if (!customElements.get('vip-zip-cell')) {
    customElements.define(
      'vip-zip-cell',
      class VipZipCell extends HTMLElement {
        connectedCallback() {
          this.zones = config.zones || [];
          this.input = this.querySelector('[data-vip-zip-input]');
          this.status = this.querySelector('[data-vip-zip-status]');

          this.querySelector('[data-vip-zip-check]').addEventListener('click', () => this.check(false));
          this.input.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.check(false);
            }
          });

          const saved = this.readSaved();
          if (saved) {
            this.input.value = saved;
            this.check(true);
          }
        }

        readSaved() {
          try {
            return localStorage.getItem('vip:delivery-zip');
          } catch {
            return null;
          }
        }

        save(zip) {
          try {
            localStorage.setItem('vip:delivery-zip', zip);
          } catch {
            /* non-essential */
          }
        }

        check(silent) {
          const zip = (this.input.value || '').trim();
          if (!/^\d{5}$/.test(zip)) {
            if (!silent) this.render('invalid', strings.invalid);
            return;
          }
          this.save(zip);
          if (!this.zones.length) {
            this.render('', '');
            return;
          }
          const served = this.zones.some((zone) => (zone.zips || []).includes(zip));
          this.render(
            served ? 'served' : 'unserved',
            (served ? strings.served : strings.unserved || '').replace('[zip]', zip)
          );
        }

        render(state, text) {
          this.status.textContent = text || '';
          this.dataset.state = state;
        }
      }
    );
  }
})();
